// Fare for hiring a driver for YOUR car. Deliberately separate from pricingEngine
// (which prices a cab, by distance): a hire is priced by how long the driver is
// needed, so the primary inputs are two datetimes.
//
// Distance never sets the price here — the customer's car burns the customer's fuel.
// It does two other jobs:
//   * beyond gate.localMaxKm the trip is OUTSTATION, whatever the customer picked.
//     Without that, a 300 km run gets booked as a 4-hour local slot.
//   * beyond gate.outstationMaxKm the trip is REFUSED. That is a driver-fatigue
//     limit, not a pricing one: past it the trip needs a second driver.
//
// The rule, as agreed:
//   distance > outstationMaxKm            -> blocked (reason OUTSTATION_KM_EXCEEDED)
//   distance > localMaxKm OR hours > 12   -> outstation slab by duration
//   otherwise                             -> local slab by duration (4/8/12)
//
// The 12-hour rule needs no distance at all: local slabs stop at 12 hours, so a
// longer booking is an away job even when the car never leaves the city — and it
// still classifies correctly when the distance lookup fails.
//   trip OVERLAPS night hours             -> + nightHalt x midnights crossed
//                                            (none for a hire that ends before midnight)
//   trip ENTIRELY INSIDE night hours      -> + nightRidePct% instead of nightHalt
//
// A night is never charged twice: overlap buys a halt, containment buys the uplift,
// and the two are mutually exclusive.
//
// This mirrors backend/utils/hireRates.js. Change a slab name in one, change both.

export const HIRE_TIERS = {
  LOCAL_4: "LOCAL_4",
  LOCAL_8: "LOCAL_8",
  LOCAL_12: "LOCAL_12",
  OUTSTATION_4: "OUTSTATION_4",
  OUTSTATION_8: "OUTSTATION_8",
  OUTSTATION_12: "OUTSTATION_12",
  OUTSTATION_24: "OUTSTATION_24",
};

export const HIRE_BLOCK_REASONS = {
  OUTSTATION_KM_EXCEEDED: "OUTSTATION_KM_EXCEEDED",
};

// The longest local booking. Past this a trip is outstation on duration alone,
// whatever the distance: nobody drives 14 hours inside one city.
export const LOCAL_MAX_HOURS = 12;

const LOCAL_LADDER = [
  { maxHours: 4,  key: "local4hr",  tier: HIRE_TIERS.LOCAL_4,  label: "Local - up to 4 hrs" },
  { maxHours: 8,  key: "local8hr",  tier: HIRE_TIERS.LOCAL_8,  label: "Local - up to 8 hrs" },
  { maxHours: 12, key: "local12hr", tier: HIRE_TIERS.LOCAL_12, label: "Local - up to 12 hrs" },
];

const OUTSTATION_LADDER = [
  { maxHours: 4,  key: "outstation4hr",  tier: HIRE_TIERS.OUTSTATION_4,  label: "Outstation - up to 4 hrs" },
  { maxHours: 8,  key: "outstation8hr",  tier: HIRE_TIERS.OUTSTATION_8,  label: "Outstation - up to 8 hrs" },
  { maxHours: 12, key: "outstation12hr", tier: HIRE_TIERS.OUTSTATION_12, label: "Outstation - up to 12 hrs" },
  { maxHours: 24, key: "outstation24hr", tier: HIRE_TIERS.OUTSTATION_24, label: "Outstation - up to 24 hrs" },
];

export const DEFAULT_HIRE_GATE = {
  localMaxKm: 80,
  outstationMaxKm: 500,
  nightStart: "22:00",
  nightEnd: "05:00",
  nightRidesEnabled: true,
  maxNightRidePct: 25,
};

// The driver's own rate, and only that. There is deliberately NO fallback to the
// platform reference: a blank rate is filled with the admin default at registration
// and stored on the driver, so by pricing time every approved driver has their own.
// Falling back at display time would mask a genuinely missing rate on an older
// record and quote a price the driver never agreed to.
//
// Returns null when absent, which the caller must surface as "price on request"
// rather than as zero.
function resolveRate(key, rates) {
  const own = Number(rates?.[key]);
  if (Number.isFinite(own) && own > 0) return own;
  return null;
}

// Legacy records priced outstation as a single per-day rate. Treat it as the 24-hour
// slab so a driver who has not re-saved since the slab split still quotes.
function resolveOutstationRate(key, rates) {
  const direct = resolveRate(key, rates);
  if (direct !== null) return direct;
  if (key === "outstation24hr") {
    return resolveRate("outstationDay", rates) ?? resolveRate("outstationRate", rates);
  }
  return null;
}

function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Night windows wrap midnight (22:00 → 05:00), so "inside" is two ranges, not one.
function isMinuteInNight(minuteOfDay, startMin, endMin) {
  if (startMin === null || endMin === null) return false;
  if (startMin === endMin) return false;
  return startMin < endMin
    ? minuteOfDay >= startMin && minuteOfDay < endMin
    : minuteOfDay >= startMin || minuteOfDay < endMin;
}

/**
 * How the trip relates to the night window.
 *
 * "contained" means every minute of the trip is night — a 23:00→04:00 run. That is a
 * night ride and takes the percentage uplift.
 * "overlaps" means the trip touches night without being wholly inside it — a
 * 21:00→05:00 run. That is a night the driver spends away, and takes a halt.
 *
 * Sampled per minute rather than by endpoints: a trip can start and end in daylight
 * and still run right through the night, and endpoint checks miss exactly that.
 */
export function classifyNight(pickup, drop, gate = DEFAULT_HIRE_GATE) {
  const startMin = toMinutes(gate.nightStart ?? DEFAULT_HIRE_GATE.nightStart);
  const endMin = toMinutes(gate.nightEnd ?? DEFAULT_HIRE_GATE.nightEnd);
  if (startMin === null || endMin === null) return { contained: false, nights: 0 };

  const totalMinutes = Math.round((drop.getTime() - pickup.getTime()) / 60000);
  if (totalMinutes <= 0) return { contained: false, nights: 0 };

  // A very long trip is night-containing by definition and cannot be night-contained,
  // so cap the scan rather than walk a week minute by minute.
  const scanLimit = Math.min(totalMinutes, 60 * 24 * 3);

  let anyNight = false;
  let allNight = true;
  for (let i = 0; i < scanLimit; i += 1) {
    const at = new Date(pickup.getTime() + i * 60000);
    const inNight = isMinuteInNight(at.getHours() * 60 + at.getMinutes(), startMin, endMin);
    if (inNight) anyNight = true;
    else allNight = false;
    if (anyNight && !allNight && totalMinutes > scanLimit) break;
  }
  if (totalMinutes > scanLimit) allNight = false;

  const contained = anyNight && allNight;

  // Nights crossed, counted on calendar dates: a 23:00→01:00 trip crosses one
  // midnight despite lasting two hours, and that is a night spent away.
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const midnights = Math.round((startOfDay(drop) - startOfDay(pickup)) / 86400000);

  // A night halt is for a night the driver spends out: one per midnight crossed (user,
  // Oct 2026). A hire that only runs into night hours and ends before midnight (18:00 →
  // 22:30) is an evening job - it used to pay a full halt.
  const nights = contained ? 0 : (anyNight ? midnights : 0);

  return { contained, nights };
}

function pickSlab(ladder, hours, rates, resolver) {
  for (const step of ladder) {
    if (hours <= step.maxHours) {
      const rate = resolver(step.key, rates);
      if (rate === null) return null;
      return { ...step, rate, units: 1 };
    }
  }
  // Past the longest slab, repeat it: a 40-hour outstation is two 24-hour days.
  const last = ladder[ladder.length - 1];
  const rate = resolver(last.key, rates);
  if (rate === null) return null;
  return { ...last, rate, units: Math.max(1, Math.ceil(hours / last.maxHours)) };
}

/**
 * Returns a fare breakdown, a block, or null.
 *
 *   null                      -> cannot price (missing input or missing rate).
 *                                Show "price on request", never a placeholder zero.
 *   { blocked: true, ... }    -> the trip must not be booked; `message` is ready to
 *                                show against the From/To pickers.
 *   otherwise                 -> a priced breakdown.
 */
export function computeHireFare({
  pickupDateTime,
  dropDateTime,
  distanceKm = null,
  // Google's estimate for the drive back, in minutes. Counted into the billed
  // duration because the driver's day does not end at the customer's door.
  returnDurationMins = 0,
  // ROUND or ONE_WAY. On a round trip the end time is "back home by" - the drive back is
  // already inside it, so it is not added again (user, Oct 2026: a 10 am - 2 pm local
  // hire was billed as 8 hours). One-way still adds the driver's way home.
  tripType = "",
  rates,
  gate = DEFAULT_HIRE_GATE,
  fromLabel = "",
  toLabel = "",
}) {
  if (!pickupDateTime || !dropDateTime) return null;

  const pickup = new Date(pickupDateTime);
  const drop = new Date(dropDateTime);
  if (Number.isNaN(pickup.getTime()) || Number.isNaN(drop.getTime())) return null;

  const ms = drop.getTime() - pickup.getTime();
  if (ms <= 0) return null;

  // One-way: the driver is not free at the drop - they still have to get home, so
  // returnDurationMins (Distance Matrix, the way back) is added to the billed duration.
  // Round trip: the end time is when the customer is back home with the driver, so the
  // way back is already inside it and nothing is added.
  const isRound = String(tripType || "").toUpperCase().startsWith("ROUND");
  const returnMs = isRound ? 0 : Math.max(0, Number(returnDurationMins) || 0) * 60000;
  const hours = (ms + returnMs) / 3600000;
  const returnHours = returnMs / 3600000;

  const km = Number.isFinite(Number(distanceKm)) ? Number(distanceKm) : null;
  const localMaxKm = Number(gate.localMaxKm ?? DEFAULT_HIRE_GATE.localMaxKm);
  const outstationMaxKm = Number(gate.outstationMaxKm ?? DEFAULT_HIRE_GATE.outstationMaxKm);

  // Refuse before pricing. Past this distance the trip is not a pricing question.
  if (km !== null && Number.isFinite(outstationMaxKm) && km > outstationMaxKm) {
    const from = fromLabel || "Pickup";
    const to = toLabel || "drop";
    return {
      blocked: true,
      reason: HIRE_BLOCK_REASONS.OUTSTATION_KM_EXCEEDED,
      distanceKm: km,
      limitKm: outstationMaxKm,
      message: `${from} to ${to} is ${Math.round(km)} km, which is more than the ${outstationMaxKm} km limit for a single driver. Please choose a destination under ${outstationMaxKm} km.`,
    };
  }

  // Two independent boundaries, either of which makes a trip outstation:
  //   * distance beyond the city's local km limit, and
  //   * duration beyond 12 hours, which applies with NO reference to distance —
  //     local slabs stop at 12, and a 20-hour booking is an away job even if the
  //     car never leaves the city.
  const beyondLocalKm = km !== null && Number.isFinite(localMaxKm) && km > localMaxKm;
  const beyondLocalHours = hours > LOCAL_MAX_HOURS;
  const isOutstation = beyondLocalKm || beyondLocalHours;

  const slab = isOutstation
    ? pickSlab(OUTSTATION_LADDER, hours, rates, resolveOutstationRate)
    : pickSlab(LOCAL_LADDER, hours, rates, resolveRate)
      // Only reachable when the driver has not set the matching local slab; the
      // outstation ladder is the honest fallback rather than no price at all.
      || pickSlab(OUTSTATION_LADDER, hours, rates, resolveOutstationRate);

  if (!slab) return null;

  const base = slab.rate * slab.units;
  const breakdown = [{
    label: slab.units > 1 ? `${slab.label} × ${slab.units}` : slab.label,
    amount: base,
  }];

  // Nights are counted to the driver's actual finish, return leg included: a driver
  // still on the road at 2am is away that night whether or not the customer is aboard.
  const effectiveEnd = new Date(drop.getTime() + returnMs);
  const { contained, nights } = classifyNight(pickup, effectiveEnd, gate);

  let nightHaltTotal = 0;
  let nightRideTotal = 0;
  const nightRidePct = Number(gate.nightRidesEnabled === false ? 0 : (rates?.nightRidePct || 0)) || 0;

  if (contained && nightRidePct > 0) {
    // Wholly inside night hours: a premium on the price, not a halt. The driver is
    // working the night, not sleeping through it away from home.
    nightRideTotal = Math.round((base * nightRidePct) / 100);
    breakdown.push({ label: `Night ride +${nightRidePct}%`, amount: nightRideTotal });
  } else if (!contained && nights > 0) {
    const haltRate = resolveRate("nightHalt", rates);
    // A missing halt rate is not an error — it means the driver charges nothing for
    // it. Quoting nothing at all here would hide an otherwise valid price.
    if (haltRate !== null) {
      nightHaltTotal = haltRate * nights;
      breakdown.push({ label: `Night halt × ${nights}`, amount: nightHaltTotal });
    }
  }

  return {
    blocked: false,
    tier: slab.tier,
    category: isOutstation ? "OUTSTATION" : "LOCAL",
    hours,
    // Split out so the UI can explain why a 33-hour booking was billed at 24+ hours.
    returnHours,
    bookedHours: ms / 3600000,
    distanceKm: km,
    units: slab.units,
    nights,
    nightRide: contained && nightRidePct > 0,
    nightRidePct: contained ? nightRidePct : 0,
    total: base + nightHaltTotal + nightRideTotal,
    breakdown,
  };
}

export function formatHireFare(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "";
  return `₹${Math.round(Number(amount)).toLocaleString("en-IN")}`;
}
